<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import { success } from '@/components/icons/icons';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import type { CompletedEventArgs } from '@/views/InstallationView/types';
import InstallationStep from './InstallationStep.vue';

interface InstallationStepActivationProps {
  csoPortalUri: string;
}
const props = defineProps<InstallationStepActivationProps>();
const emit = defineEmits<{
  completed: [args: CompletedEventArgs];
}>();

const authenticationStore = useAuthenticationStore();

function activate() {
  if (authenticationStore.session?.companyId && authenticationStore.session?.ipin) {
    // sanitize csoPortalUri, in case it ends with a slash
    const csoPortalUri = props.csoPortalUri.endsWith('/')
      ? props.csoPortalUri.slice(0, -1)
      : props.csoPortalUri;

    // redirect to activation page, which will in turn auto-redirect back here
    location.replace(
      `${csoPortalUri}/installation-activation/${authenticationStore.session.companyId}/${authenticationStore.session.ipin}?redirectUri=${location.href}`,
    );
  } else {
    console.error('no CompanyId and ipin is set');
  }
}
async function next() {
  emit('completed', { shouldUpdate: false, preventNextNavigation: false });
}
</script>

<template>
  <InstallationStep>
    <template #content>
      <h4 class="mb-4 w-lg-66">
        {{ $t('ActivateInstallation') }}
      </h4>
      <p class="w-lg-66">{{ $t('ActivationMsg') }}</p>
    </template>
    <template #footer>
      <v-alert
        v-if="authenticationStore?.session?.isInstallationActivated"
        class="mt-8 fit-content"
        type="success"
      >
        <v-icon class="mr-2">{{ success }}</v-icon>
        {{ $t('ActivationSuccessful') }}
        <template #prepend> </template>
      </v-alert>
      <VSpacer />
      <Button
        class="mt-8 ml-2"
        variant="primary"
        :label="$t('Activate')"
        :disabled="authenticationStore?.session?.isInstallationActivated"
        @click="activate"
      />
      <Button
        variant="secondary"
        class="mt-8 ml-2"
        :disabled="!authenticationStore?.session?.isInstallationActivated"
        :label="$t('Next')"
        @click="next"
      />
    </template>
  </InstallationStep>
</template>
